import { AppState } from '../interface';
import { SessionAppState } from '../../../interface';
import { getCMSConfig } from '../../../util/cms';
import {
    getSessionStorageItemJson,
    setSessionStorageItemJson,
} from '@vfde-sails/storage';
import { STORAGE_KEY } from '../../../constants';
import {
    Domains,
    ProductType,
    QueryNames,
} from '../../../enum';
import { getExpiryDate } from '../../../util/date';
import { debugInfo } from '../../../util/debug';
import { getQueryParam } from '@vfde-sails/utils';
import { isValidId } from '../../../util/typeGuard';
import { initialState } from '../reducer';
import { getAddressCookie } from '@vfde-sails/avc-core';
import { isObjectDeeplyChanged } from '../../../util/state';
import { State } from '../selector';

/**
 * Saves the AppState to the session, while adding an expiry date of 1 day
 * Does not save availability state for logged in customers
 *
 * @param state - State to be saved
 */
export const saveStateToSession = (state: State) => {
    const {
        isTVProductSelected,
        selectedTVProduct,
        availableTariffs,
        address,
        alternativeTariff,
        selectedTariffId,
        customer,
        bannerId,
        marketability,
        availability,
    } = state;

    const expiry = getExpiryDate(1).toISOString();

    const sessionAppState: SessionAppState = {
        [ProductType.CABLE]: !customer && address ? {
            isTVProductSelected,
            availableTariffs,
            selectedTVProduct,
            address,
            alternativeTariff,
            selectedTariffId,
            bannerId,
            marketability,
            availability,
            expiry,
        } : {
            selectedTariffId,
            expiry,
        },
    };

    setSessionStorageItemJson(STORAGE_KEY, sessionAppState);
};

/**
 * Updates the state from the CMS options on pageload
 * Sets `selectedTariffId`
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
export const getStateFromPageOptions = (draftState: AppState) => {
    const { selectedTariffId, isYoung } = getCMSConfig();

    draftState.selectedTariffId = selectedTariffId;
    draftState.isYoung = Boolean(isYoung === 'true');

    return draftState;
};

/**
 * Updates the state from the saved session on pageload
 * Does not update the state if session state is expired
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
export const getStateFromSession = (draftState: AppState) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);
    const appState: Partial<AppState> | undefined = sessionState?.cable;
    const addressCookie = getAddressCookie();

    if (!sessionState || !appState || isObjectDeeplyChanged(addressCookie, appState.address)) {
        debugInfo('Failed to load state from SessionStorage');

        return draftState;
    }

    if (new Date(sessionState.cable.expiry) < new Date()) {
        debugInfo('Session expired');

        return draftState;
    }

    return { ...draftState, ...appState };
};

/**
 * Updates the state from the query parameters on pageload
 * Sets `isGigakombi` and `selectedTariffId` if they are present
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
export const getStateFromQuery = (draftState: AppState) => {
    const tariffId = getQueryParam(QueryNames.PRODUCT);
    const isGigakombi = getQueryParam(QueryNames.GIGAKOMBI);

    if (isGigakombi === 'true') {
        draftState.isGigakombi = true;
    }

    const availableIds = draftState.availableTariffs.length
        ? draftState.availableTariffs.map(tariff => tariff.id)
        : Object.values(getCMSConfig().tariffIds.kdg);

    if (isValidId(tariffId, availableIds)) {
        draftState.selectedTariffId = tariffId;
    }

    return draftState;
};

/**
 * Load state from sessionStorage, additionalPageOptions, query params, cookies.
 *
 * @returns AppState
 */
export const loadInitialAppState = (): Partial<AppState> => {
    try {
        // we need a deep copy of initialState because 'immer' makes it read-only
        let draftState: AppState = JSON.parse(JSON.stringify(initialState));

        draftState = getStateFromPageOptions(draftState);
        draftState = getStateFromSession(draftState);
        draftState = getStateFromQuery(draftState);

        return draftState;
    }
    catch {
        debugInfo('Error while parsing app state');

        return initialState;
    }
};
